import classNames from 'classnames';
import { memo } from 'react';
import isEqual from 'react-fast-compare';

import {
  FileStatusButton,
  FileStatusPanelItem,
  FileStatusSubtitle,
  FileStatusThumbnail,
  FileStatusTitle,
  type FileTrackingItem,
} from '@air/feature-file-status/ui';
import { getSubtitleWithProgress } from '@air/feature-file-status/utils';
import { Close, Pause, Skip } from '@air/next-icons';
import { UploadStatus } from '@air/redux-uploader';

import { SHARED_PANEL_ITEM_CLASS_NAMES } from '../../constants';

export type FileStatusTrackingPanelListItemProgressingProps = {
  item: FileTrackingItem;
  onCancel?: (item: FileTrackingItem) => void;
  onPause?: (item: FileTrackingItem) => void;
  onSkip?: (item: FileTrackingItem) => void;
};

export const FileStatusTrackingPanelListItemProgressing = memo(
  ({
    item,
    item: {
      title,
      ext,
      status,
      onRowClick,
      previewLoader,
      thumbnailSource,
      avatar,
      subtitle,
      sizeInBytes,
      currentBytesUploaded,
    },
    onSkip,
    onCancel,
    onPause,
  }: FileStatusTrackingPanelListItemProgressingProps) => (
    <FileStatusPanelItem
      avatar={avatar ?? <FileStatusThumbnail ext={ext} status={status} thumbnailSource={thumbnailSource} />}
      buttons={
        <>
          {onPause && (
            <FileStatusButton appearance="ghost" label="Pause" onClick={() => onPause(item)}>
              <Pause className="size-4" />
            </FileStatusButton>
          )}
          {onSkip && (
            <FileStatusButton appearance="ghost" label="Skip" onClick={() => onSkip(item)}>
              <Skip className="size-4" />
            </FileStatusButton>
          )}
          {onCancel && (
            <FileStatusButton appearance="ghost" label="Cancel" onClick={() => onCancel(item)}>
              <Close className="size-4" />
            </FileStatusButton>
          )}
        </>
      }
      className={classNames('hover:bg-grey-3', SHARED_PANEL_ITEM_CLASS_NAMES, !!onRowClick && 'cursor-pointer')}
      onClick={onRowClick}
      previewLoader={previewLoader}
      subtitle={
        status === UploadStatus.uploading ? (
          <FileStatusSubtitle className="truncate">
            {subtitle ??
              getSubtitleWithProgress({
                sizeInBytes,
                title: 'Uploading...',
                uploadedBytes: currentBytesUploaded,
              })}
          </FileStatusSubtitle>
        ) : (
          <FileStatusSubtitle>Queued</FileStatusSubtitle>
        )
      }
      title={<FileStatusTitle className="text-12">{title}</FileStatusTitle>}
    />
  ),
  isEqual,
);

FileStatusTrackingPanelListItemProgressing.displayName = 'FileStatusTrackingPanelListItemProgressing';
